<template>
  <v-container v-if="isLoading" fluid fill-width class="ma-0 pa-2">
    <v-row class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0"> <loading-spinner /> </v-col>
    </v-row>
  </v-container>

  <v-container v-else fluid class="ma-0 pa-0">
    <v-row class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0">
        <v-row class="ma-0 pa-0 mt-4">
          <v-text-field
            v-model="var_search_text"
            label="Show variables with a domain or name that matches this text:"
            prepend-inner-icon="mdi-magnify"
            clearable
            class="ma-0 pa-0 mr-4"
          >
          </v-text-field>

          <v-tooltip v-if="enableVariableSelection" top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <v-chip
                :color="getNumVariablesColor(selectedVariables.length)"
                :text-color="getNumVariablesTextColor(selectedVariables.length)"
                class="title mt-1 mr-4"
                v-on="{ ...tooltip }"
              >
                {{ selectedVariables.length }} / {{ variables.length }} selected
              </v-chip>
            </template>
            <span
              >{{ selectedVariables.length }} variables out of
              {{ variables.length }} variables are selected.</span
            >
          </v-tooltip>

          <v-tooltip top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <v-chip color="primary" class="title mt-1" v-on="{ ...tooltip }">
                {{ visibleVariableCount }} / {{ variables.length }} shown
              </v-chip>
            </template>
            <span
              >{{ visibleVariableCount }} variables out of
              {{ variables.length }} variables are displayed.</span
            >
          </v-tooltip>
        </v-row>
      </v-col>
    </v-row>

    <v-row class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0">
        <v-chip
          label
          icon
          color="#4caf50"
          class="font-weight-bold white--text ma-0 pa-3 mb-2"
        >
          <v-icon center class="ma-0 pa-0 mr-2 white--text"
            >mdi-alert-circle</v-icon
          >
          Click on any row below to view a full-size plot of the data for that
          variable.
        </v-chip>

        <v-data-table
          v-model="selectedVariablesSelect"
          :search="var_search_text"
          :headers="headers"
          :items="variables"
          :custom-sort="customSort"
          :custom-filter="customFilter"
          :show-select="enableVariableSelection"
          item-key="abbreviation"
          must-sort
          class="tr-hover ma-0 pa-0"
          disable-pagination
          hide-default-footer
          @pagination="setVisibleVariableCount"
          @click:row="onGraphClick"
        >
          <template v-slot:item.category_icon="{ item }">
            <td class="subtitle-1 text-xs-left py-2">
              <v-row fill-height align="center">
                <img
                  :src="'/images/' + item.category + '-icon-128.png'"
                  :title="item.category"
                  style="height:2.5em;"
                  @error="missingDomainFallback"
                />
              </v-row>
            </td>
          </template>
          <template v-slot:item.category="{ item }">
            <td
              class="subtitle-1 text-xs-left text-wrap py-2"
              style="line-height: 1.25em;"
            >
              <span>{{ item.category }}</span>
            </td>
          </template>
          <template v-slot:item.abbreviation="{ item }">
            <td
              class="subtitle-1 text-xs-left text-wrap py-2"
              style="line-height: 1.25em;"
            >
              <span>{{
                useLongScaleNames ? item.label : item.abbreviation
              }}</span>
            </td>
          </template>
          <template v-slot:item.data_category="{ item }">
            <td
              class="subtitle-1 text-xs-left text-wrap py-2"
              style="line-height: 1.25em;"
            >
              <span>{{ item.data_category }}</span>
            </td>
          </template>
          <template v-slot:item.category_icon2="{ item }">
            <td class="subtitle-1 text-xs-left">
              <v-container class="ma-0 pa-0 mt-2">
                <v-row class="ma-0 pa-0" justify="center">
                  <histogram-sparkline
                    v-if="isCollection"
                    :id="'collection-' + collection.id"
                    :key="'sl-' + 'collection-' + collection.id + '-' + item.id"
                    :type="item.type"
                    :scale-id="item.id"
                    :data-category="item.data_category"
                  />
                  <histogram-sparkline
                    v-else
                    :id="'study-' + dataset.id"
                    :key="'sl-' + 'study-' + dataset.id + '-' + item.id"
                    :type="item.type"
                    :scale-id="item.id"
                    :data-category="item.data_category"
                  />
                </v-row>

                <v-row justify="center" class="ma-0 pa-0">
                  <span
                    :style="
                      'color: ' +
                        getStudyVariableColor(getStudyVariableCount(item))
                    "
                  >
                    {{ getStudyVariableCount(item) }}</span
                  >
                </v-row>
              </v-container>
            </td>
          </template>
          <template v-slot:item.description="{ item }">
            <td
              class="subtitle-1 text-xs-left text-wrap py-2"
              style="line-height: 1.25em;"
            >
              <span>{{ item.description }}</span>
            </td>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" width="50%">
      <variable-summary-chart :variable="clickedVar" @close="dialog = false" />
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { state, actions } from '@/store/modules/datasetSummary/types';
import HistogramSparkline from '@/components/DatasetSummary/HistogramSparkline.vue';
import VariableSummaryChart from '@/components/DatasetSummary/VariableSummaryChart.vue';
import { getNumVariablesColor, getNumVariablesTextColor } from '@/utils/colors';
import {
  strCompare,
  numericSuffixStrCompare,
  sortScales,
  missingDomainFallback,
} from '@/utils/helpers';

export default {
  components: {
    HistogramSparkline,
    VariableSummaryChart,
  },
  props: {},
  data() {
    return {
      isLoading: true,
      dialog: false,
      clickedVar: null,
      var_search_text: '',
      visibleVariableCount: 0,
      study_variable_counts: { subject: {}, observation: {} },
      headers: [
        {
          text: '',
          value: 'category_icon',
          sortable: false,
          filterable: false,
          class: 'text-subtitle-1 font-weight-bold',
          width: '2%',
        },
        {
          text: 'Domain',
          value: 'category',
          sortable: true,
          filterable: true,
          class: 'text-subtitle-1 font-weight-bold',
          width: '10%',
        },
        {
          text: 'Variable',
          value: 'abbreviation',
          sortable: true,
          filterable: true,
          class: 'text-subtitle-1 font-weight-bold',
          width: '20%',
        },
        {
          text: 'Graph',
          value: 'category_icon2',
          sortable: false,
          filterable: false,
          class: 'text-subtitle-1 font-weight-bold',
          width: '6%',
        },
        {
          text: 'Type',
          value: 'data_category',
          sortable: true,
          filterable: true,
          class: 'text-subtitle-1 font-weight-bold',
          width: '6%',
        },
        {
          text: 'Description',
          value: 'description',
          sortable: false,
          filterable: true,
          class: 'text-subtitle-1 font-weight-bold descr',
          width: '56%',
        },
      ],
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      dataset: state.DATASET,
      collection: state.COLLECTION,
      variables: state.VARIABLES,
      isCollection: state.IS_COLLECTION,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
      enableVariableSelection: state.ENABLE_VARIABLE_SELECTION,
      selectedVariables: state.SELECTED_VARIABLES,
    }),
    selectedVariablesSelect: {
      get() {
        if (this.enableVariableSelection) {
          return this.selectedVariables;
        } else {
          return [];
        }
      },
      set(value) {
        if (this.enableVariableSelection) {
          this.setSelectedVariables(value);
        }
      },
    },
  },
  watch: {
    enableVariableSelection(flag) {
      if (flag) {
        this.selectedVariablesSelect = this.variables;
      } else {
        this.setSelectedVariables(this.variables);
      }
    },
  },
  async created() {
    this.isLoading = true;

    if (!this.isCollection) {
      this.study_variable_counts = await this.getVariableCounts();
    }

    this.isLoading = false;
  },
  methods: {
    ...mapActions('datasetSummary', {
      getVariableCounts: actions.GET_VARIABLE_COUNTS,
      setSelectedVariables: actions.SET_SELECTED_VARIABLES,
    }),
    getNumVariablesColor,
    getNumVariablesTextColor,
    onGraphClick(item) {
      this.clickedVar = item;
      this.dialog = true;
    },
    setVisibleVariableCount(pagination) {
      this.visibleVariableCount = pagination.itemsLength;
    },
    customSort(items, index, isDesc) {
      let cs_fn = function(a, b) {
        return index[0] === 'label'
          ? numericSuffixStrCompare(a.label, b.label)
          : strCompare(a[index], b[index]);
      };
      let ss = index == '' ? sortScales(items) : items.sort(cs_fn);
      return isDesc == 'true' ? ss.reverse() : ss;
    },
    customFilter(value, search, item) {
      const lc_search = search == null ? null : search.toLowerCase();
      return (
        lc_search == null ||
        item.category.toLowerCase().indexOf(lc_search) !== -1 ||
        item.abbreviation.toLowerCase().indexOf(lc_search) !== -1 ||
        item.label.toLowerCase().indexOf(lc_search) !== -1
      );
    },
    getStudyVariableColor(nSubjects) {
      if (nSubjects <= 25) {
        return '#F83008';
      } else {
        return 'black';
      }
    },
    getStudyVariableCount(v) {
      if (this.isCollection) {
        return this.collection.num_subjects;
      } else {
        let study_id = this.dataset.id;
        let var_id = v.id;
        let var_type = v['type'] == 'subject' ? 'subject' : 'observation';
        var key = var_id + ':' + study_id;
        var count = 0;
        if (key in this.study_variable_counts[var_type]) {
          count = this.study_variable_counts[var_type][key];
        }
        return count;
      }
    },
  },
};
</script>

<style scoped>
.tr-hover >>> tbody tr :hover {
  cursor: pointer;
}
td.nowrap {
  word-break: keep-all;
  white-space: nowrap;
}
.unselectable {
  height: 1.5em !important;
  opacity: 0.4;
  cursor: default;
}

.unselectable:active {
  pointer-events: none;
  cursor: default !important;
}
</style>
